import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StockService } from '../../../../services/stock.service';
import { WarehousesService } from '../../../../services/warehouses.service';
import { Stock } from '../../../../models/Stock';
import { Warehouse } from '../../../../models/Warehouse';

@Component({
  selector: 'app-stock-warehouse-modal',
  templateUrl: './stock-warehouse-modal.component.html',
  styleUrls: ['./stock-warehouse-modal.component.css']
})
export class StockWarehouseModalComponent implements OnInit {
  @Input() stock: Stock | null = null;
  @Input() isVisible = false;
  @Output() close = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();

  form: FormGroup;
  warehouses: Warehouse[] = [];
  isLoading = false;
  isSubmitting = false;
  actionType: 'assign' | 'transfer' | 'unassign' = 'assign';

  constructor(
    private fb: FormBuilder,
    private stockService: StockService,
    private warehousesService: WarehousesService
  ) {
    this.form = this.fb.group({
      warehouseId: [null, Validators.required],
      location: ['', Validators.maxLength(200)]
    });
  }

  ngOnInit(): void {
    this.loadWarehouses();
  }

  ngOnChanges(): void {
    if (this.stock) {
      this.initializeForm();
    }
  }

  loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: result => {
        this.warehouses = result.items || [];
      },
      error: (_: any) => {
        this.warehouses = [];
      }
    });
  }

  initializeForm(): void {
    if (this.stock) {
      this.form.patchValue({
        warehouseId: this.stock.warehouseId || null,
        location: this.stock.location || ''
      });

      // Determinar o tipo de ação baseado no estado atual
      if (this.stock.warehouseId) {
        this.actionType = 'transfer';
      } else {
        this.actionType = 'assign';
      }
    }
  }

  setActionType(type: 'assign' | 'transfer' | 'unassign'): void {
    this.actionType = type;
    
    if (type === 'unassign') {
      this.form.patchValue({
        warehouseId: null,
        location: ''
      });
    } else if (this.stock) {
      this.form.patchValue({
        warehouseId: this.stock.warehouseId || null,
        location: this.stock.location || ''
      });
    }
  }

  getActionTitle(): string {
    switch (this.actionType) {
      case 'assign':
        return 'Vincular ao Almoxarifado';
      case 'transfer':
        return 'Transferir para Almoxarifado';
      case 'unassign':
        return 'Desvincular do Almoxarifado';
      default:
        return 'Gerenciar Almoxarifado';
    }
  }

  getActionButtonText(): string {
    switch (this.actionType) {
      case 'assign':
        return 'Vincular';
      case 'transfer':
        return 'Transferir';
      case 'unassign':
        return 'Desvincular';
      default:
        return 'Confirmar';
    }
  }

  getActionDescription(): string {
    switch (this.actionType) {
      case 'assign':
        return 'Vincule este estoque a um almoxarifado específico.';
      case 'transfer':
        return 'Transfira este estoque para outro almoxarifado.';
      case 'unassign':
        return 'Remova a vinculação deste estoque com o almoxarifado atual.';
      default:
        return '';
    }
  }

  isFormValid(): boolean {
    if (this.actionType === 'unassign') {
      return true;
    }
    return this.form.valid;
  }

  submit(): void {
    if (!this.stock || !this.isFormValid()) {
      return;
    }

    this.isSubmitting = true;
    const formValue = this.form.value;

    let operation: any;

    switch (this.actionType) {
      case 'assign':
        operation = this.stockService.assignToWarehouse(
          this.stock.id, 
          formValue.warehouseId, 
          formValue.location
        );
        break;
      case 'transfer':
        operation = this.stockService.transferToWarehouse(
          this.stock.id, 
          formValue.warehouseId, 
          formValue.location
        );
        break;
      case 'unassign':
        operation = this.stockService.unassignFromWarehouse(this.stock.id);
        break;
      default:
        this.isSubmitting = false;
        return;
    }

    operation.subscribe({
      next: (_: any) => {
        this.isSubmitting = false;
        this.success.emit();
        this.closeModal();
      },
      error: (_: any) => {
        this.isSubmitting = false;
      }
    });
  }

  closeModal(): void {
    this.form.reset();
    this.actionType = 'assign';
    this.close.emit();
  }

  getSelectedWarehouseName(): string {
    const warehouseId = this.form.get('warehouseId')?.value;
    const warehouse = this.warehouses.find(w => w.id === warehouseId);
    return warehouse ? `${warehouse.name} (${warehouse.code})` : '';
  }

  getCurrentWarehouseName(): string {
    if (!this.stock?.warehouseId) {
      return 'Nenhum';
    }
    const warehouse = this.warehouses.find(w => w.id === this.stock?.warehouseId);
    return warehouse ? `${warehouse.name} (${warehouse.code})` : 'Desconhecido';
  }
}
